import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, Clock } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const DatePicker = () => {
  const { state, actions } = useApp();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('preset');
  const dropdownRef = useRef(null);

  const { periodSelection } = state;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const presetOptions = [
    { key: 'current_month', label: 'Mois en cours', icon: '📅' },
    { key: 'current_quarter', label: 'Trimestre en cours', icon: '📊' },
    { key: 'fiscal_year', label: 'Exercice en cours', icon: '📑' },
    { key: '6_months', label: '6 prochains mois', icon: '📈' }
  ];

  const handlePresetSelect = (preset) => {
    actions.setPeriodSelection({
      type: 'preset',
      preset,
      startDate: null,
      endDate: null,
      singleDate: null
    });
    setIsOpen(false);
  };

  const handleCustomDateChange = (field, value) => {
    const newSelection = {
      ...periodSelection,
      type: 'custom',
      [field]: value
    };
    actions.setPeriodSelection(newSelection);
  };

  const handleSingleDateChange = (value) => {
    actions.setPeriodSelection({
      type: 'single',
      preset: null,
      startDate: null,
      endDate: null,
      singleDate: value
    });
  };

  const getDisplayText = () => {
    if (periodSelection.type === 'preset') {
      const preset = presetOptions.find(p => p.key === periodSelection.preset);
      return preset ? preset.label : 'Période sélectionnée';
    } else if (periodSelection.type === 'custom') {
      const start = periodSelection.startDate ? format(new Date(periodSelection.startDate), 'dd/MM/yyyy', { locale: fr }) : '';
      const end = periodSelection.endDate ? format(new Date(periodSelection.endDate), 'dd/MM/yyyy', { locale: fr }) : '';
      if (start && end) return `${start} - ${end}`;
      if (start) return `À partir du ${start}`;
      if (end) return `Jusqu'au ${end}`;
      return 'Période personnalisée';
    } else if (periodSelection.type === 'single') {
      return periodSelection.singleDate 
        ? format(new Date(periodSelection.singleDate), 'dd/MM/yyyy', { locale: fr })
        : 'Date unique';
    }
    
    return 'Mois en cours';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
      >
        <Calendar size={16} className="text-gray-400" />
        <span className="text-gray-700">{getDisplayText()}</span>
        <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('preset')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                activeTab === 'preset' 
                  ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock size={16} className="inline mr-2" />
              Prédéfinies
            </button>
            <button
              onClick={() => setActiveTab('custom')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                activeTab === 'custom' 
                  ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calendar size={16} className="inline mr-2" />
              Période
            </button>
            <button
              onClick={() => setActiveTab('single')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                activeTab === 'single' 
                  ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calendar size={16} className="inline mr-2" />
              Date unique
            </button>
          </div>

          <div className="p-4">
            {activeTab === 'preset' && (
              <div className="space-y-2">
                {presetOptions.map((option) => (
                  <button
                    key={option.key}
                    onClick={() => handlePresetSelect(option.key)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 text-sm rounded-lg text-left hover:bg-gray-50 ${
                      periodSelection.type === 'preset' && periodSelection.preset === option.key
                        ? 'bg-primary-50 text-primary-700 border border-primary-200'
                        : 'text-gray-700'
                    }`}
                  >
                    <span className="text-base">{option.icon}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}

            {activeTab === 'custom' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de début
                  </label>
                  <input
                    type="date"
                    value={periodSelection.startDate || ''}
                    onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de fin
                  </label>
                  <input
                    type="date"
                    value={periodSelection.endDate || ''}
                    onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-full px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                >
                  Appliquer la période
                </button>
              </div>
            )}

            {activeTab === 'single' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sélectionner une date
                  </label>
                  <input
                    type="date"
                    value={periodSelection.singleDate || ''}
                    onChange={(e) => handleSingleDateChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-full px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                >
                  Appliquer la date
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker; 